import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  Inject,
  TemplateRef,
  ViewChild,
  ViewContainerRef
} from '@angular/core';
import { PoprToastPackage } from './popr-toast.package';
import { BehaviorSubject, Observable, of, Subject } from 'rxjs';
import { PoprPriority } from '../popr-priority.enum';
import { delay, finalize, tap } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';

/**
 * UI container for toast pop-ups.  Wraps either string or TemplateRef content and mediates opening and closing of the
 * toast.
 *
 * @Input TemplateRef | String | String as JSON for translate pipe {translateKey: key_pair_for_i18b_message}
 *
 */
@Component({
  selector: 'fdx-popr-toast',
  templateUrl: './popr-toast.component.html',
  styleUrls: ['./popr-toast.component.css'],
  standalone : false
})
export class PoprToastComponent implements AfterViewInit {
  private onClose = new Subject<void>();

  @ViewChild('dynamicContainer', { read: ViewContainerRef })
  dynamicContentContainer!: ViewContainerRef;
  isOpen$ = new BehaviorSubject<boolean>(false);

  constructor(
    @Inject('dynamicContent') private dynamicContent: string | TemplateRef<any>,
    private changeDetectorRef: ChangeDetectorRef,
    public poprToastPackage: PoprToastPackage,
    private translateService: TranslateService
  ) {}

  ngAfterViewInit(): void {
    try {
      this.changeDetectorRef.detach();
      if (typeof this.dynamicContent === 'string') {
        let content;
        try {
          content = JSON.parse(this.dynamicContent);
        }
        catch (err) {
        }
        if(content?.translateKey) {
          this.translateService.get(content.translateKey).subscribe((translatedMsg) => {
            (this.dynamicContentContainer.element.nativeElement as HTMLElement).append(translatedMsg);
          });
        }
        else {
          (this.dynamicContentContainer.element.nativeElement as HTMLElement).append(this.dynamicContent);
        }
      } else if (this.dynamicContent instanceof TemplateRef) {
        this.dynamicContentContainer.createEmbeddedView(this.dynamicContent);
      }
    } finally {
      this.changeDetectorRef.reattach();
      this.changeDetectorRef.detectChanges();
    }
  }

  /**
   * Open this toast
   */
  open(): void {
    of(true).pipe(
      delay(200 /* allow css animation to complete */),
      tap(open => this.toggleToast(open)),
    ).subscribe();
  }

  /**
   * Close this toast
   */
  close(): void {
    of(false).pipe(
      tap(close => this.toggleToast(close)),
      delay(180 /* allow css animation to complete */),
      finalize(() => {
        this.onClose.next();
        this.onClose.complete();
      })
    ).subscribe();
  }

  onClose$(): Observable<void> {
    return this.onClose.asObservable();
  }

  get classForPriority(): string {
    // coerce into number for switch/case
    const priority =  (typeof this.poprToastPackage.config?.priority === 'number')
      ? this.poprToastPackage.config?.priority
      : +PoprPriority[this.poprToastPackage.config?.priority];

    switch (priority) {
      case PoprPriority.URGENT:
      case PoprPriority.ERROR:
        return 'fdx-c-notification--error';
      case PoprPriority.SUCCESS:
        return 'fdx-c-notification--success';
      case PoprPriority.INFO:
        return 'fdx-c-notification--info';
      default:
        return '';
    }
  }

  private toggleToast(isOpen: boolean): void {
    this.isOpen$.next(isOpen);
    this.changeDetectorRef.detectChanges();
  }
}
